import { Check, Copy, Expand } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";
import ReactMarkdown, { Components, ExtraProps } from "react-markdown";
import remarkBreaks from "remark-breaks";
import gfm from "remark-gfm";
import validator from "validator";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

import dark from "react-syntax-highlighter/dist/esm/styles/hljs/a11y-dark";
import light from "react-syntax-highlighter/dist/esm/styles/hljs/a11y-light";

import SyntaxHighlighter from "react-syntax-highlighter";

import { useThemeContext } from "../../context/ThemeContext";
import { InlineEntity } from "../InlineEntity";
import { StreamingTextRenderer } from "../StreamingTextRenderer";

export interface MarkdownProps {
  children: string;
  // Optional streaming mode - applies glow effect to text as it appears
  isStreaming?: boolean;
  components?: Components;
  className?: string;
}

const EntityRenderer = React.memo(
  ({
    children,
    entityRenderer,
  }: {
    children?: React.ReactNode;
    entityRenderer?: (content: string) => React.ReactNode;
  }) => {
    // If a custom entity renderer is provided, use it
    if (entityRenderer) {
      if (typeof children === "string") {
        return entityRenderer(children);
      }

      if (Array.isArray(children)) {
        return children.map((child, index) => {
          if (typeof child === "string") {
            return (
              <React.Fragment key={index}>
                {entityRenderer(child)}
              </React.Fragment>
            );
          }
          return child;
        });
      }

      return children;
    }

    // If no custom entity renderer is provided, use the InlineEntity component
    return <InlineEntity>{children}</InlineEntity>;
  },
);

EntityRenderer.displayName = "EntityRenderer";

export const withEntityRenderer = <P extends { children?: React.ReactNode }>(
  Component: React.ComponentType<P>,
  entityRenderer?: (content: string) => React.ReactNode,
) => {
  return (props: P) => {
    const { children, ...rest } = props;
    return (
      <Component {...(rest as P)}>
        <EntityRenderer entityRenderer={entityRenderer}>
          {children}
        </EntityRenderer>
      </Component>
    );
  };
};

const getLabelFromChildren = (children: any) => {
  let label = "";

  React.Children.map(children, (child) => {
    if (typeof child === "string") {
      label += child;
    }
  });

  return label;
};

// Type guard function to check if an object has props
const hasProps = (obj: unknown): obj is { props: unknown } => {
  return obj !== null && typeof obj === "object" && "props" in obj;
};

// Type guard for code block props
const isCodeBlockProps = (
  props: unknown,
): props is {
  className?: string;
  children?: string;
} => {
  return (
    props !== null &&
    typeof props === "object" &&
    ("className" in props || "children" in props)
  );
};

function columnStyle(
  props: React.ClassAttributes<HTMLTableDataCellElement> &
    React.TdHTMLAttributes<HTMLTableDataCellElement> &
    ExtraProps,
): React.CSSProperties {
  const textContent = getLabelFromChildren(props.children);

  if (validator.isEmail(textContent)) {
    return {
      whiteSpace: "nowrap",
    };
  }

  if (validator.isMobilePhone(textContent)) {
    return {
      whiteSpace: "nowrap",
    };
  }

  if (textContent.length < 20) {
    return {
      whiteSpace: "nowrap",
      minWidth: `${textContent.length}px`,
    };
  }

  if (textContent.length > 100) {
    return {
      minWidth: "40ch",
    };
  }

  return {
    minWidth: `${textContent.length * 0.4}ch`,
  };
}

function TableWrapper({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <div className="lg:absolute md:-right-12 md:top-1">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsOpen(true)}
              >
                <Expand />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Expand Table View</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <div className="overflow-auto">{children}</div>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-[95vw] w-full max-h-[95vh]">
          <DialogHeader>
            <DialogTitle>Table View</DialogTitle>
          </DialogHeader>
          <div className="overflow-auto h-full">{children}</div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Memoized code component for syntax highlighting
const CodeBlock = React.memo((props: any) => {
  const { mode } = useThemeContext();
  const { children, className, node, ...rest } = props;

  const language = useMemo(() => {
    const match = /language-(\w+)/.exec(className || "");
    return match ? match[1] : null;
  }, [className]);

  const theme = useMemo(() => {
    return mode === "dark" ? dark : light;
  }, [mode]);

  if (language) {
    return (
      <SyntaxHighlighter
        {...rest}
        PreTag="div"
        children={String(children).replace(/\n$/, "")}
        language={language}
        style={theme}
        className="background-none"
      />
    );
  }

  return (
    <code {...rest} className={className}>
      {children}
    </code>
  );
});

CodeBlock.displayName = "CodeBlock";

// Memoized pre component with copy functionality
const PreBlock = React.memo((props: any) => {
  const { children, ...rest } = props;
  const [isCopied, setIsCopied] = useState(false);

  // Extract language and code directly from children props
  const { language, code } = useMemo(() => {
    const propsChildren = props.children;

    // Early return if not a code block
    if (!hasProps(propsChildren)) {
      return { language: "plaintext", code: "" };
    }

    if (!isCodeBlockProps(propsChildren.props)) {
      return { language: "plaintext", code: "" };
    }

    const childProps = propsChildren.props;
    const content = childProps.children;

    // Skip markdown blocks
    if (typeof content === "string" && content.startsWith("```markdown")) {
      return { language: "plaintext", code: "" };
    }

    // Extract language from className
    const match = /language-(\w+)/.exec(childProps.className || "");

    if (
      Array.isArray(match) &&
      typeof match[1] === "string" &&
      typeof content === "string"
    ) {
      return { language: match[1], code: content.trim() };
    }

    return { language: "plaintext", code: "" };
  }, [props.children]);

  const handleCopy = useCallback(() => {
    if (code) {
      navigator.clipboard.writeText(code);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }
  }, [code]);

  return (
    <div className="relative my-4 border rounded-md overflow-hidden">
      <div className="bg-muted text-xs font-mono px-3 py-1 flex justify-between items-center">
        <div className="text-muted-foreground">{language}</div>

        {code && (
          <div>
            <Button variant="ghost" size="xs" onClick={handleCopy}>
              {isCopied ? (
                <>
                  <Check />
                  Copied
                </>
              ) : (
                <>
                  <Copy />
                  Copy
                </>
              )}
            </Button>
          </div>
        )}
      </div>
      <pre
        {...rest}
        className="border-none bg-background text-sm border p-2 overflow-auto text-foreground whitespace-pre-wrap"
      >
        {children}
      </pre>
    </div>
  );
});

PreBlock.displayName = "PreBlock";

// Recursive function to wrap all text nodes with streaming effect
const wrapTextWithStreaming = (children: React.ReactNode): React.ReactNode => {
  return React.Children.map(children, (child) => {
    // If it's a string, wrap it with StreamingTextRenderer
    if (typeof child === "string") {
      return <StreamingTextRenderer>{child}</StreamingTextRenderer>;
    }

    // If it's a React element with children, recurse
    if (React.isValidElement(child)) {
      const props = child.props as any;
      if (props && props.children) {
        return React.cloneElement(
          child,
          props,
          wrapTextWithStreaming(props.children),
        );
      }
    }

    // Otherwise return as-is
    return child;
  });
};

// HOC to wrap component with streaming text effect
const withStreamingText = <P extends { children?: React.ReactNode }>(
  Component: React.ComponentType<P>,
  isStreaming?: boolean,
) => {
  if (!isStreaming) {
    return Component;
  }

  return (props: P) => {
    const { children, ...rest } = props;
    return (
      <Component {...(rest as P)}>{wrapTextWithStreaming(children)}</Component>
    );
  };
};

export const createDefaultMarkdownComponents = (
  entityRenderer?: (content: string) => React.ReactNode,
  isStreaming?: boolean,
): Components => ({
  h1: withStreamingText(
    withEntityRenderer(
      (props: any) => (
        <h1 className="text-2xl my-3 text-foreground" {...props} />
      ),
      entityRenderer,
    ),
    isStreaming,
  ),
  h2: withStreamingText(
    withEntityRenderer(
      (props: any) => (
        <h2 className="text-xl my-2 text-foreground" {...props} />
      ),
      entityRenderer,
    ),
    isStreaming,
  ),
  h3: withStreamingText(
    withEntityRenderer(
      (props: any) => (
        <h3 className="text-lg my-2 text-foreground" {...props} />
      ),
      entityRenderer,
    ),
    isStreaming,
  ),
  h4: withStreamingText(
    withEntityRenderer(
      (props: any) => (
        <h4 className="text-base my-2 text-foreground" {...props} />
      ),
      entityRenderer,
    ),
    isStreaming,
  ),
  h5: withStreamingText(
    withEntityRenderer(
      (props: any) => <h5 className="my-1 text-foreground" {...props} />,
      entityRenderer,
    ),
    isStreaming,
  ),
  h6: withStreamingText(
    withEntityRenderer(
      (props: any) => (
        <h6 className="text-xs my-1 text-foreground" {...props} />
      ),
      entityRenderer,
    ),
    isStreaming,
  ),
  p: withStreamingText(
    withEntityRenderer(
      (props: any) => <p className="my-3 " {...props} />,
      entityRenderer,
    ),
    isStreaming,
  ),
  ul: withStreamingText(
    withEntityRenderer(
      (props: any) => <ul className="list-disc my-2 mb-6 ml-8 " {...props} />,
      entityRenderer,
    ),
    isStreaming,
  ),
  ol: withStreamingText(
    withEntityRenderer(
      (props: any) => (
        <ol
          className={`list-decimal my-2 mb-6 ml-8  ${isStreaming ? "animate-glow-in" : ""}`}
          {...props}
        />
      ),
      entityRenderer,
    ),
    isStreaming,
  ),
  li: withStreamingText(
    withEntityRenderer(
      (props: any) => (
        <li
          className={` my-2 ${isStreaming ? "animate-glow-in" : ""}`}
          {...props}
        />
      ),
      entityRenderer,
    ),
    isStreaming,
  ),
  hr: () => <hr className="my-4 border-t border-border" />,
  blockquote: withStreamingText(
    withEntityRenderer(
      (props: any) => (
        <blockquote
          className="border-l-4 border-border pl-4 mb-4 italic text-muted-foreground"
          {...props}
        />
      ),
      entityRenderer,
    ),
    isStreaming,
  ),
  code: CodeBlock,
  pre: PreBlock,
  table: withStreamingText(
    withEntityRenderer(
      (props: any) => (
        <TableWrapper>
          <Table {...props} />
        </TableWrapper>
      ),
      entityRenderer,
    ),
    isStreaming,
  ),
  thead: withStreamingText(
    withEntityRenderer(
      (props: any) => <TableHeader {...props} />,
      entityRenderer,
    ),
    isStreaming,
  ),
  tbody: withStreamingText(
    withEntityRenderer(
      (props: any) => <TableBody {...props} />,
      entityRenderer,
    ),
    isStreaming,
  ),
  tr: withStreamingText(
    withEntityRenderer((props: any) => <TableRow {...props} />, entityRenderer),
    isStreaming,
  ),
  th: withStreamingText(
    withEntityRenderer(
      (props: any) => (
        <TableHead
          {...props}
          style={{
            ...columnStyle(props),
            ...props.style,
          }}
        />
      ),
      entityRenderer,
    ),
    isStreaming,
  ),
  td: withStreamingText(
    withEntityRenderer(
      (props) => (
        <TableCell
          {...props}
          style={{
            ...columnStyle(props),
            verticalAlign: "top",
            ...props.style,
          }}
        />
      ),
      entityRenderer,
    ),
    isStreaming,
  ),
  a: withStreamingText(
    withEntityRenderer(
      (props: any) => (
        <a
          className="text-teal dark:text-teal underline hover:text-[color-mix(in_oklab,var(--teal)_78%,var(--ink))] dark:hover:text-[color-mix(in_oklab,var(--teal)_55%,var(--paper))]"
          target="_blank"
          rel="noopener noreferrer"
          {...props}
        />
      ),
      entityRenderer,
    ),
    isStreaming,
  ),
});

const remarkPlugins = [gfm, remarkBreaks];

// Create component sets once outside the component
const defaultComponents = createDefaultMarkdownComponents(undefined, false);
const streamingComponents = createDefaultMarkdownComponents(undefined, true);

export function Markdown({
  children,
  isStreaming = false,
  components,
  className,
}: MarkdownProps) {
  // Delay turning off streaming mode to allow animations to complete
  const [internalStreaming, setInternalStreaming] = React.useState(isStreaming);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    if (isStreaming) {
      // Immediately enable streaming mode
      setInternalStreaming(true);
      // Clear any pending timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    } else if (internalStreaming) {
      // Delay disabling streaming mode to let animations complete
      timeoutRef.current = setTimeout(() => {
        setInternalStreaming(false);
      }, 5000); // Match animation duration (5s)
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isStreaming, internalStreaming]);

  // Select pre-built components based on internal streaming state

  const baseComponents = useMemo(() => {
    if (internalStreaming) {
      return {
        ...streamingComponents,
        ...components,
      };
    }

    return {
      ...defaultComponents,
      ...components,
    };
  }, [internalStreaming, components]);

  return (
    <ReactMarkdown
      className={className}
      components={baseComponents}
      remarkPlugins={remarkPlugins}
    >
      {children}
    </ReactMarkdown>
  );
}
